import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ExternalLink, Eye, EyeOff, FileText, Loader2, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { useAuth } from '@/contexts/useAuth';
import { LW_THEME_CLASS } from '../shared/InvestorThemeScope';
import { InvestorService } from '@/services/investorService';
import { InvestorCredentialService } from '@/services/investorCredentialService';
import { DocumentService } from '@/services/documentService';
import { UserService } from '@/services/userService';
import { isDocumentVisibleForRoles } from '@/utils/investor/documentVisibility';
import { resolveLinkedDocumentLabel } from '@/utils/investor/linkedDocumentLabel';
import { safePortalUrl } from '@/utils/investor/validation';
import { createComponentLogger } from '@/utils/logger';
import { toast } from 'sonner';
import type { Investor, InvestorDocument, InvestorOperationalContact, InvestorUserCredential } from '@/types/investor';
import type { User } from '@/types/user';
import { EncompassContactsSection } from '../EncompassContactsSection';

const log = createComponentLogger('CredentialsTab', 'investors-v2');

interface CredentialsTabProps {
  investor: Investor;
  organizationId: string;
  variant: 'processing' | 'closing';
  /** edit_investors — gates the admin-managed sections. */
  canManage: boolean;
  /** Propagates the freshly hydrated investor after an admin save. */
  onInvestorUpdated: (updated: Investor) => void;
}

/** The admin-editable subset of fields this tab manages (shared + variant-specific). */
function buildDraft(inv: Investor) {
  return {
    wholesaleWebsite: inv.wholesaleWebsite ?? '',
    correspondentWebsite: inv.correspondentWebsite ?? '',
    howToGetCredentials: inv.howToGetCredentials ?? '',
    portalNotes: inv.portalNotes ?? '',
    closingWholesaleWebsite: inv.closingWholesaleWebsite ?? '',
    closingCorrespondentWebsite: inv.closingCorrespondentWebsite ?? '',
    closingHowToGetCredentials: inv.closingHowToGetCredentials ?? '',
    closingPortalNotes: inv.closingPortalNotes ?? '',
    fhaLenderId: inv.fhaLenderId ?? '',
    vaLenderId: inv.vaLenderId ?? '',
    mersOrgId: inv.mersOrgId ?? '',
    taxId: inv.taxId ?? '',
    docPlanCodes: inv.docPlanCodes ?? '',
    operationalContacts: (inv.operationalContacts ?? []).map((c) => ({ ...c })),
    closingOperationalContacts: (inv.closingOperationalContacts ?? []).map((c) => ({ ...c })),
    submissionInstructions: inv.submissionInstructions ?? '',
    submissionNotes: inv.submissionNotes ?? '',
    deliveryInstructions: inv.deliveryInstructions ?? '',
    deliveryNotes: inv.deliveryNotes ?? '',
    closingRelianceLetterRequired: inv.closingRelianceLetterRequired ?? false,
    deliveryMethod: (inv.deliveryMethod ?? '') as '' | 'Investor Connect' | 'Other',
    deliveryMethodOther: inv.deliveryMethodOther ?? ''
  };
}
type Draft = ReturnType<typeof buildDraft>;
type ContactSectionId = 'contacts' | 'closing-contacts';
type ContactDraftKey = 'operationalContacts' | 'closingOperationalContacts';

const CONTACT_DRAFT_KEY_BY_SECTION: Record<ContactSectionId, ContactDraftKey> = {
  contacts: 'operationalContacts',
  'closing-contacts': 'closingOperationalContacts'
};

const Section: React.FC<{ title: string; children: React.ReactNode; action?: React.ReactNode }> = ({
  title, children, action
}) => (
  <Card className="rounded-lg border-border shadow-none">
    <CardHeader className="flex-row items-center justify-between space-y-0 px-3.5 pt-2.5 pb-2">
      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      {action}
    </CardHeader>
    <CardContent className="px-3.5 pb-2.5 pt-0">{children}</CardContent>
  </Card>
);

const Field: React.FC<{ label: string; value: string; editing: boolean; onChange: (v: string) => void; placeholder?: string }> = ({
  label, value, editing, onChange, placeholder
}) => (
  <div className="space-y-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {editing ? (
      <Input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <p className="text-sm text-foreground">{value || <span className="text-muted-foreground italic">Not set</span>}</p>
    )}
  </div>
);

/** Like Field, but renders a saved http/https URL as a truncating new-tab link (item 8). */
const UrlField: React.FC<{ label: string; value: string; editing: boolean; onChange: (v: string) => void; placeholder?: string }> = ({
  label, value, editing, onChange, placeholder
}) => {
  const href = safePortalUrl(value);
  return (
    <div className="space-y-1 min-w-0">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {editing ? (
        <Input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      ) : !value ? (
        <p className="text-sm text-muted-foreground italic">Not set</p>
      ) : href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={value}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <span className="truncate">{value}</span>
          <ExternalLink className="h-3 w-3 flex-shrink-0" />
        </a>
      ) : (
        <p className="text-sm text-foreground break-all">{value}</p>
      )}
    </div>
  );
};

export const CredentialsTab: React.FC<CredentialsTabProps> = ({
  investor, organizationId, variant, canManage, onInvestorUpdated
}) => {
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => buildDraft(investor));

  // Reset the draft whenever the investor identity or its data changes.
  useEffect(() => {
    setDraft(buildDraft(investor));
    setEditingSection(null);
  }, [investor]);

  const set = useCallback(<K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const updateContact = (
    draftKey: ContactDraftKey,
    idx: number,
    patch: Partial<InvestorOperationalContact>
  ) => {
    setDraft((d) => ({
      ...d,
      [draftKey]: d[draftKey].map((c, i) => (i === idx ? { ...c, ...patch } : c))
    }));
  };
  const addContact = (draftKey: ContactDraftKey) =>
    setDraft((d) => ({ ...d, [draftKey]: [...d[draftKey], {}] }));
  const removeContact = (draftKey: ContactDraftKey, idx: number) =>
    setDraft((d) => ({ ...d, [draftKey]: d[draftKey].filter((_, i) => i !== idx) }));

  const handleSave = async (section: string) => {
    setSaving(true);
    try {
      const patch: Partial<Investor> = {};
      if (section === 'portal') {
        if (variant === 'closing') {
          patch.closingWholesaleWebsite = draft.closingWholesaleWebsite || undefined;
          patch.closingCorrespondentWebsite = draft.closingCorrespondentWebsite || undefined;
          patch.closingHowToGetCredentials = draft.closingHowToGetCredentials || undefined;
          patch.closingPortalNotes = draft.closingPortalNotes || undefined;
        } else {
          patch.wholesaleWebsite = draft.wholesaleWebsite || undefined;
          patch.correspondentWebsite = draft.correspondentWebsite || undefined;
          patch.howToGetCredentials = draft.howToGetCredentials || undefined;
          patch.portalNotes = draft.portalNotes || undefined;
        }
      }
      if (section === 'lender-ids') {
        patch.fhaLenderId = draft.fhaLenderId || undefined;
        patch.vaLenderId = draft.vaLenderId || undefined;
        patch.mersOrgId = draft.mersOrgId || undefined;
        patch.taxId = draft.taxId || undefined;
        patch.docPlanCodes = draft.docPlanCodes || undefined;
      }
      if (section === 'contacts' || section === 'closing-contacts') {
        const draftKey = CONTACT_DRAFT_KEY_BY_SECTION[section];
        patch[draftKey] = draft[draftKey].filter(
          (c) => c.label || c.name || c.email || c.phone
        );
      }
      if (section === 'submission') {
        patch.submissionInstructions = draft.submissionInstructions || undefined;
        patch.submissionNotes = draft.submissionNotes || undefined;
      }
      if (section === 'reliance') {
        patch.closingRelianceLetterRequired = draft.closingRelianceLetterRequired;
      }
      if (section === 'delivery-method') {
        patch.deliveryMethod = draft.deliveryMethod ? draft.deliveryMethod : null;
        patch.deliveryMethodOther = draft.deliveryMethod === 'Other' ? draft.deliveryMethodOther || undefined : undefined;
      }
      if (section === 'delivery-instructions') {
        patch.deliveryInstructions = draft.deliveryInstructions || undefined;
      }
      if (section === 'delivery-notes') {
        patch.deliveryNotes = draft.deliveryNotes || undefined;
      }

      const updated = await InvestorService.updateInvestorFields(investor.id, patch, organizationId);
      if (updated) {
        onInvestorUpdated(updated);
      }
      setEditingSection(null);
      toast.success('Saved');
    } catch (e) {
      log.error('Failed to save credentials tab', { investorId: investor.id }, e as Error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const sectionAction = (section: string) => {
    if (!canManage) {
      return null;
    }
    const editing = editingSection === section;
    if (editing) {
      return (
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => {
 setDraft(buildDraft(investor)); setEditingSection(null); 
}} disabled={saving}>
            <X className="mr-1 h-4 w-4" /> Cancel
          </Button>
          <Button size="sm" onClick={() => void handleSave(section)} disabled={saving}>
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Save
          </Button>
        </div>
      );
    }
    return (
      <Button size="sm" variant="outline" onClick={() => setEditingSection(section)} disabled={Boolean(editingSection)}>
        <Pencil className="mr-1 h-4 w-4" /> Edit
      </Button>
    );
  };

  const portalEditing = editingSection === 'portal';
  const lenderIdsEditing = editingSection === 'lender-ids';
  const submissionEditing = editingSection === 'submission';
  const relianceEditing = editingSection === 'reliance';
  const deliveryMethodEditing = editingSection === 'delivery-method';
  const deliveryInstructionsEditing = editingSection === 'delivery-instructions';
  const deliveryNotesEditing = editingSection === 'delivery-notes';
  const wholesalePortal = variant === 'closing'
    ? draft.closingWholesaleWebsite
    : draft.wholesaleWebsite;
  const correspondentPortal = variant === 'closing'
    ? draft.closingCorrespondentWebsite
    : draft.correspondentWebsite;
  const credentialInstructions = variant === 'closing'
    ? draft.closingHowToGetCredentials
    : draft.howToGetCredentials;
  const portalNotes = variant === 'closing'
    ? draft.closingPortalNotes
    : draft.portalNotes;

  const renderOperationalContactsSection = (sectionId: ContactSectionId) => {
    const draftKey = CONTACT_DRAFT_KEY_BY_SECTION[sectionId];
    const contacts = draft[draftKey];
    const contactsEditing = editingSection === sectionId;

    return (
      <Section
        title="Operational Contacts"
        action={
          contactsEditing
            ? (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => addContact(draftKey)}>
                  <Plus className="mr-1 h-4 w-4" /> Add
                </Button>
                {sectionAction(sectionId)}
              </div>
            )
            : sectionAction(sectionId)
        }
      >
        {contacts.length === 0 && !contactsEditing ? (
          <p className="text-sm italic text-muted-foreground">No operational contacts.</p>
        ) : (
          <div className="space-y-3">
            {contacts.map((contact, idx) => {
              const fieldIdBase = `${sectionId}-contact-${idx}`;
              return (
                <div key={idx} className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                  {contactsEditing ? (
                    <>
                      <div>
                        <Label htmlFor={`${fieldIdBase}-label`} className="sr-only">Label</Label>
                        <Input
                          id={`${fieldIdBase}-label`}
                          placeholder="Label (e.g. Lock Desk)"
                          value={contact.label ?? ''}
                          onChange={(e) => updateContact(draftKey, idx, { label: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`${fieldIdBase}-name`} className="sr-only">Name</Label>
                        <Input
                          id={`${fieldIdBase}-name`}
                          placeholder="Name"
                          value={contact.name ?? ''}
                          onChange={(e) => updateContact(draftKey, idx, { name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`${fieldIdBase}-email`} className="sr-only">Email</Label>
                        <Input
                          id={`${fieldIdBase}-email`}
                          placeholder="Email"
                          value={contact.email ?? ''}
                          onChange={(e) => updateContact(draftKey, idx, { email: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-2">
                        <div className="min-w-0 flex-1">
                          <Label htmlFor={`${fieldIdBase}-phone`} className="sr-only">Phone</Label>
                          <Input
                            id={`${fieldIdBase}-phone`}
                            placeholder="Phone"
                            value={contact.phone ?? ''}
                            onChange={(e) => updateContact(draftKey, idx, { phone: e.target.value })}
                          />
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => removeContact(draftKey, idx)}
                          aria-label="Remove contact"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="sm:col-span-4 text-sm">
                      <span className="font-medium text-foreground">{contact.label || 'Contact'}</span>
                      {contact.name ? ` · ${contact.name}` : ''}
                      {contact.email ? ` · ${contact.email}` : ''}
                      {contact.phone ? ` · ${contact.phone}` : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>
    );
  };

  return (
    <div className="space-y-4 lw-detail-stack">
      <Section title="Portal Access" action={sectionAction('portal')}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {investor.canDoWholesale && (
            <UrlField label="Wholesale portal" value={wholesalePortal} editing={portalEditing}
              onChange={(v) => variant === 'closing' ? set('closingWholesaleWebsite', v) : set('wholesaleWebsite', v)} placeholder="https://broker.example.com" />
          )}
          {investor.canGoCorrespondent && (
            <UrlField label="Correspondent portal" value={correspondentPortal} editing={portalEditing}
              onChange={(v) => variant === 'closing' ? set('closingCorrespondentWebsite', v) : set('correspondentWebsite', v)} placeholder="https://portal.example.com" />
          )}
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">How to get credentials</Label>
            {portalEditing
              ? <Textarea value={credentialInstructions} rows={2} onChange={(e) => variant === 'closing' ? set('closingHowToGetCredentials', e.target.value) : set('howToGetCredentials', e.target.value)} />
              : <p className="whitespace-pre-wrap text-sm text-foreground">{credentialInstructions || <span className="italic text-muted-foreground">Not set</span>}</p>}
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Notes / specifics</Label>
            {portalEditing
              ? <Textarea value={portalNotes} rows={2} onChange={(e) => variant === 'closing' ? set('closingPortalNotes', e.target.value) : set('portalNotes', e.target.value)} />
              : <p className="whitespace-pre-wrap text-sm text-foreground">{portalNotes || <span className="italic text-muted-foreground">Not set</span>}</p>}
          </div>
        </div>
      </Section>

      <UserCredentialsSection
        investorId={investor.id}
        organizationId={organizationId}
      />

      {variant === 'processing' && (
        <Section title="Submission Instructions & Notes" action={sectionAction('submission')}>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Submission instructions</Label>
              {submissionEditing
                ? <Textarea rows={3} value={draft.submissionInstructions} onChange={(e) => set('submissionInstructions', e.target.value)} />
                : <p className="whitespace-pre-wrap text-sm text-foreground">{draft.submissionInstructions || <span className="italic text-muted-foreground">Not set</span>}</p>}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Notes</Label>
              {submissionEditing
                ? <Textarea rows={2} value={draft.submissionNotes} onChange={(e) => set('submissionNotes', e.target.value)} />
                : <p className="whitespace-pre-wrap text-sm text-foreground">{draft.submissionNotes || <span className="italic text-muted-foreground">Not set</span>}</p>}
            </div>
          </div>
        </Section>
      )}

      <Section title="Lender IDs" action={sectionAction('lender-ids')}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {lenderIdsEditing || draft.fhaLenderId ? <Field label="FHA Lender ID" value={draft.fhaLenderId} editing={lenderIdsEditing} onChange={(v) => set('fhaLenderId', v)} /> : null}
          {lenderIdsEditing || draft.vaLenderId ? <Field label="VA Lender ID" value={draft.vaLenderId} editing={lenderIdsEditing} onChange={(v) => set('vaLenderId', v)} /> : null}
          {lenderIdsEditing || draft.mersOrgId ? <Field label="MERS Org ID" value={draft.mersOrgId} editing={lenderIdsEditing} onChange={(v) => set('mersOrgId', v)} /> : null}
          {lenderIdsEditing || draft.taxId ? <Field label="Tax ID #" value={draft.taxId} editing={lenderIdsEditing} onChange={(v) => set('taxId', v)} /> : null}
          {lenderIdsEditing || draft.docPlanCodes ? <Field label="Doc Plan Codes" value={draft.docPlanCodes} editing={lenderIdsEditing} onChange={(v) => set('docPlanCodes', v)} /> : null}
          {!lenderIdsEditing && !draft.fhaLenderId && !draft.vaLenderId && !draft.mersOrgId && !draft.taxId && !draft.docPlanCodes && (
            <p className="text-sm italic text-muted-foreground">No lender IDs.</p>
          )}
        </div>
      </Section>

      {variant === 'processing' ? (
        <>
        {renderOperationalContactsSection('contacts')}
        </>
      ) : (
        <>
        <Section title="Reliance Letter" action={sectionAction('reliance')}>
          {relianceEditing ? (
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border"
                checked={draft.closingRelianceLetterRequired}
                onChange={(e) => set('closingRelianceLetterRequired', e.target.checked)}
              />
              Reliance letter required before purchase
            </label>
          ) : (
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Reliance letter</Label>
              {draft.closingRelianceLetterRequired ? (
                <span className="flex items-center gap-1 text-sm font-medium text-green-600 dark:text-green-500">
                  <Check className="h-4 w-4" /> Required
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">Not required</span>
              )}
            </div>
          )}
        </Section>

        <Section title="Delivery Method" action={sectionAction('delivery-method')}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Delivery method</Label>
                {deliveryMethodEditing ? (
                  <Select value={draft.deliveryMethod || undefined} onValueChange={(v) => set('deliveryMethod', v as Draft['deliveryMethod'])}>
                    <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                    <SelectContent className={LW_THEME_CLASS}>
                      <SelectItem value="Investor Connect">Investor Connect</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-foreground">{draft.deliveryMethod || <span className="italic text-muted-foreground">Not set</span>}</p>
                )}
              </div>
              {draft.deliveryMethod === 'Other' && (
                <Field label="Delivery instructions to investor" value={draft.deliveryMethodOther} editing={deliveryMethodEditing}
                  onChange={(v) => set('deliveryMethodOther', v)} />
              )}
            </div>
          </div>
        </Section>

        <Section title="Delivery Instructions" action={sectionAction('delivery-instructions')}>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Delivery instructions</Label>
              {deliveryInstructionsEditing
                ? <Textarea rows={3} value={draft.deliveryInstructions} onChange={(e) => set('deliveryInstructions', e.target.value)} />
                : <p className="whitespace-pre-wrap text-sm text-foreground">{draft.deliveryInstructions || <span className="italic text-muted-foreground">Not set</span>}</p>}
            </div>
        </Section>

        <Section title="Notes" action={sectionAction('delivery-notes')}>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Notes</Label>
              {deliveryNotesEditing
                ? <Textarea rows={2} value={draft.deliveryNotes} onChange={(e) => set('deliveryNotes', e.target.value)} />
                : <p className="whitespace-pre-wrap text-sm text-foreground">{draft.deliveryNotes || <span className="italic text-muted-foreground">Not set</span>}</p>}
            </div>
        </Section>

        {renderOperationalContactsSection('closing-contacts')}
        </>
      )}

      {variant === 'closing' && <EncompassContactsSection investorId={investor.id} defaultOpen />}

      <LinkedDocumentsSection investorId={investor.id} variant={variant} />
    </div>
  );
};

/**
 * Documents an admin has linked to this tab (Processing/Closing), read-only here.
 * Respects the same UI-level role visibility as the Documents tab.
 */
const LinkedDocumentsSection: React.FC<{ investorId: string; variant: 'processing' | 'closing' }> = ({
  investorId, variant
}) => {
  const { user } = useAuth();
  const userRoles = useMemo(
    () => ((user?.roles as string[] | undefined) ?? []).map((r) => r.toLowerCase()),
    [user]
  );
  const [docs, setDocs] = useState<InvestorDocument[]>([]);

  useEffect(() => {
    let active = true;
    DocumentService.getInvestorDocuments(investorId)
      .then((list) => {
 if (active) {
 setDocs(list); 
} 
})
      .catch((e) => {
        if (active) {
 setDocs([]); 
}
        log.error('Failed to load linked documents', { investorId }, e as Error);
      });
    return () => {
 active = false; 
};
  }, [investorId]);

  const linked = docs.filter(
    (d) => (d.linkedTabs ?? []).includes(variant) && isDocumentVisibleForRoles(d, userRoles)
  );
  if (linked.length === 0) {
    return null;
  }

  return (
    <Section title="Linked Documents">
      <div className="space-y-2">
        {linked.map((d) => {
          const label = resolveLinkedDocumentLabel(d);
          const originalFileName = d.originalFileName?.trim();
          return (
            <a
              key={d.id}
              href={d.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-md border border-border p-2 text-sm hover:bg-muted/40"
            >
              <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <span className="truncate text-foreground" title={originalFileName || label}>{label}</span>
              <ExternalLink className="ml-auto h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
            </a>
          );
        })}
      </div>
    </Section>
  );
};

/**
 * Per-user portal credentials — exactly ONE record per user per investor (item 9).
 * Read-only by default with a redacted password + Reveal; editable only via
 * "Edit my credentials" (item 17). Admins may additionally reveal another user's
 * password (audited server-side). Passwords are never returned by `list` — only by
 * an explicit, gated `reveal`.
 */
const REDACTED_PASSWORD = '••••••••••';
const REVEAL_TIMEOUT_MS = 30_000;

interface CredentialDraft {
  username: string;
  password: string;
  notes: string;
  showPassword: boolean;
}

const UserCredentialsSection: React.FC<{ investorId: string; organizationId: string }> = ({
  investorId, organizationId
}) => {
  const { user } = useAuth();
  const isStrictAdmin = useMemo(
    () => ((user?.roles as string[] | undefined) ?? []).map((r) => r.toLowerCase()).includes('admin'),
    [user]
  );

  const [rows, setRows] = useState<InvestorUserCredential[]>([]);
  const [orgUsers, setOrgUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CredentialDraft>({ username: '', password: '', notes: '', showPassword: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [ownRevealed, setOwnRevealed] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});

  const own = useMemo(() => rows.find((r) => r.isOwn) ?? null, [rows]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, usersResult] = await Promise.all([
        InvestorCredentialService.list(investorId),
        isStrictAdmin ? UserService.getOrganizationUsers(organizationId) : Promise.resolve({ data: [] as User[], error: null })
      ]);
      setRows(list);
      setOrgUsers(usersResult.data);
      if (isStrictAdmin && !selectedUserId) {
        const firstOtherUser = usersResult.data.find((orgUser) => orgUser.id !== user?.id);
        setSelectedUserId(firstOtherUser?.id ?? '');
      }
    } catch (e) {
      log.error('Failed to load credentials', { investorId }, e as Error);
    } finally {
      setLoading(false);
    }
  }, [investorId, isStrictAdmin, organizationId, selectedUserId, user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-revert a revealed own-password after ~30s.
  useEffect(() => {
    if (ownRevealed === null) {
      return;
    }
    const timer = setTimeout(() => setOwnRevealed(null), REVEAL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [ownRevealed]);

  const beginEdit = () => {
    setOwnRevealed(null);
    setDraft({ username: own?.username ?? '', password: '', notes: own?.notes ?? '', showPassword: false });
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setDraft({ username: '', password: '', notes: '', showPassword: false });
  };

  const saveMine = async () => {
    setSaving(true);
    try {
      await InvestorCredentialService.saveMine({
        investorId,
        username: draft.username || null,
        notes: draft.notes || null,
        ...(draft.password ? { password: draft.password } : {})
      });
      toast.success('Credentials saved');
      setEditing(false);
      setOwnRevealed(null);
      await load();
    } catch (e) {
      log.error('Failed to save own credential', { investorId }, e as Error);
      toast.error('Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const revealOwn = async () => {
    if (!own) {
      return;
    }
    try {
      const pw = await InvestorCredentialService.reveal(own.id);
      setOwnRevealed(pw ?? '(none)');
    } catch {
      toast.error('Not allowed to reveal this credential');
    }
  };

  const reveal = async (credentialId: string) => {
    try {
      const pw = await InvestorCredentialService.reveal(credentialId);
      setRevealed((r) => ({ ...r, [credentialId]: pw ?? '(none)' }));
    } catch {
      toast.error('Not allowed to reveal this credential');
    }
  };

  const selectedUser = orgUsers.find((orgUser) => orgUser.id === selectedUserId);
  const selectedUserRow = rows.find((row) => row.userId === selectedUserId) ?? null;

  return (
    <Section
      title="My Portal Credentials"
      action={!editing ? (
        <Button size="sm" variant="outline" onClick={beginEdit}>
          <Pencil className="mr-1 h-4 w-4" /> Edit
        </Button>
      ) : null}
    >
      {editing ? (
        <div className="rounded-md border border-border p-2.5">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Username</Label>
              <Input value={draft.username} autoComplete="off" onChange={(e) => setDraft((d) => ({ ...d, username: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Password {own?.hasPassword ? '(leave blank to keep)' : ''}</Label>
              <div className="flex gap-2">
                <Input
                  type={draft.showPassword ? 'text' : 'password'}
                  value={draft.password}
                  placeholder={own?.hasPassword ? REDACTED_PASSWORD : ''}
                  autoComplete="new-password"
                  onChange={(e) => setDraft((d) => ({ ...d, password: e.target.value }))}
                />
                <Button type="button" size="icon" variant="ghost" onClick={() => setDraft((d) => ({ ...d, showPassword: !d.showPassword }))} aria-label="Toggle password visibility">
                  {draft.showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
          <div className="mt-2 space-y-1">
            <Label className="text-xs text-muted-foreground">Notes</Label>
            <Input value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} />
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={saving}><X className="mr-1 h-4 w-4" /> Cancel</Button>
            <Button size="sm" onClick={() => void saveMine()} disabled={saving}>
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" onBlur={() => setOwnRevealed(null)}>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Username</Label>
            <p className="text-sm text-foreground">{own?.username || <span className="italic text-muted-foreground">Not set</span>}</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Password</Label>
            {own?.hasPassword ? (
              <p className="flex items-center gap-2 text-sm text-foreground">
                <span className="font-mono">{ownRevealed ?? REDACTED_PASSWORD}</span>
                <button type="button" className="text-xs text-primary hover:underline" onClick={() => (ownRevealed ? setOwnRevealed(null) : void revealOwn())}>
                  {ownRevealed ? 'Hide' : 'Reveal'}
                </button>
              </p>
            ) : (
              <p className="text-sm italic text-muted-foreground">Not set</p>
            )}
          </div>
          {own?.notes && (
            <div className="space-y-1 rounded-md bg-muted/40 px-2.5 py-2 sm:col-span-2">
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <p className="text-sm text-foreground">{own.notes}</p>
            </div>
          )}
        </div>
      )}

      {isStrictAdmin && (
        <Collapsible open={adminOpen} onOpenChange={setAdminOpen} className="mt-3 border-t border-border pt-2">
          <CollapsibleTrigger asChild>
            <Button type="button" size="sm" variant="ghost" className="w-full justify-between px-2">
              <span>Admin credentials</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${adminOpen ? 'rotate-180' : ''}`} aria-hidden />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs font-medium text-muted-foreground">Reveal is audited</p>
              <Select value={selectedUserId || undefined} onValueChange={setSelectedUserId}>
                <SelectTrigger className="w-full sm:w-64"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent className={LW_THEME_CLASS}>
                  {orgUsers.filter((orgUser) => orgUser.id !== user?.id).map((orgUser) => (
                    <SelectItem key={orgUser.id} value={orgUser.id}>
                      {orgUser.fullName || orgUser.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedUser ? (
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                <div className="min-w-0">
                  <span className="font-medium text-foreground">{selectedUserRow?.username || <span className="italic text-muted-foreground">no username</span>}</span>
                  {selectedUserRow && revealed[selectedUserRow.id] !== undefined && (
                    <span className="ml-2 font-mono text-foreground">{revealed[selectedUserRow.id]}</span>
                  )}
                </div>
                {selectedUserRow?.hasPassword && revealed[selectedUserRow.id] === undefined && (
                  <Button size="sm" variant="outline" onClick={() => reveal(selectedUserRow.id)}>
                    <Eye className="mr-1 h-4 w-4" /> Reveal
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-sm italic text-muted-foreground">No user selected.</p>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}
      {loading && <p className="mt-2 text-xs text-muted-foreground">Loading…</p>}
    </Section>
  );
};

export default CredentialsTab;
